import React from "react";
import { useTranslation } from "react-i18next";

import {
  FintechArd,
  FintechArigBank,
  FintechCapitronBank,
  FintechCKBank,
  FintechGolomtBank,
  FintechHiPay,
  FintechKhanBank,
  FintechMBank,
  FintechMostMoney,
  FintechStateBank,
  FintechTDBank,
  FintechXacBank,
  FintechBogdBank,
} from "../../assets";
import { useOverlay } from "../../providers";
import { useDeviceDetect } from "../../hooks/useDevice";

// TEMP: shows the store URL in an alert right before redirecting, so we can verify
// the fallback on a real device. Set to false / remove once confirmed.
const DEBUG_STORE_FALLBACK = true;

interface FintechAppButtonsProps {
  qrCode: string;
}

interface FintechApp {
  key: string;
  image: string;
  scheme: string;
  /** Apple App Store url (store fallback link). */
  appStoreUrl: string;
  /** Google Play package name (used to build the store fallback link). */
  androidPackage: string;
}

const FintechAppButtons: React.FC<FintechAppButtonsProps> = ({ qrCode }) => {
  const { t } = useTranslation();
  const { setLoading } = useOverlay();
  const { isIOS, isAndroid } = useDeviceDetect();

  const storeUrlFor = (app: FintechApp): string | null => {
    // Pin to the Mongolian App Store — some of these apps (e.g. Arig) are only
    // published in the MN store, so a region-less link 404s in web fallbacks.
    if (isIOS) return app.appStoreUrl;
    if (isAndroid) return `https://play.google.com/store/apps/details?id=${app.androidPackage}`;
    return null;
  };

  const openFintechApp = (app: FintechApp) => {
    setLoading(true);
    const hasShrunkParams =
      app.scheme.startsWith("socialpay-payment") || app.scheme.startsWith("bogdbank");
    const url = `${app.scheme}q?qPay_QRcode=${qrCode}${hasShrunkParams ? "" : "&object_type=&object_id="}`;

    const storeUrl = storeUrlFor(app);

    // If the deep link succeeds the app takes focus and the page is hidden/unloaded;
    // that lets us tell "app opened" from "app not installed". We intentionally do
    // NOT listen to `blur`, which iOS fires for the "Cannot Open Page" error dialog
    // and would give a false positive.
    let appOpened = false;
    const markOpened = () => {
      appOpened = true;
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") markOpened();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", markOpened);

    const cleanup = () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", markOpened);
    };

    window.setTimeout(() => {
      cleanup();
      setLoading(false);

      // Still here after the timeout and the page never got backgrounded → the app
      // isn't installed. Send the user to the store to install it.
      if (appOpened || !storeUrl) return;

      // TEMP DEBUG: confirms which URL is used on-device (no console on a phone) and
      // cleanly separates this navigation from the failed custom-scheme attempt.
      // Remove `DEBUG_STORE_FALLBACK` once the store redirect is confirmed working.
      if (DEBUG_STORE_FALLBACK) {
        // eslint-disable-next-line no-alert
        window.alert(`Opening store:\n${storeUrl}`);
      }

      window.location.href = storeUrl;
    }, 2000);

    // Attempt to open the app. On iOS Safari an uninstalled custom scheme shows a
    // "Safari cannot open the page because the address is invalid" dialog — this is
    // unavoidable for custom schemes (only Apple Universal Links suppress it).
    window.location.href = url;
  };

  return (
    <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
      {fintechApps.map((fa) => (
        <button
          key={fa.key}
          onClick={() => openFintechApp(fa)}
          className="font-light flex-start flex-col p-2 gap-2 text-center leading-4.5 hover:bg-primary/25 rounded-3xl"
        >
          <img src={fa.image} className="w-16 rounded-2xl" />
          {t(`payment.fintechApps.${fa.key}`)}
        </button>
      ))}
    </div>
  );
};

const fintechApps: FintechApp[] = [
  {
    key: "mostmoney",
    image: FintechMostMoney,
    scheme: "most://",
    appStoreUrl: "https://apps.apple.com/mn/app/mostmoney/id487144325",
    androidPackage: "mn.grapecity.mostmoney",
  },
  {
    key: "tdbank",
    image: FintechTDBank,
    scheme: "tdbbankv2://",
    appStoreUrl: "https://apps.apple.com/mn/app/tdb-online/id1458831706",
    androidPackage: "mn.tdb.pay",
  },
  {
    key: "ckbank",
    image: FintechCKBank,
    scheme: "ckbank://",
    appStoreUrl: "https://apps.apple.com/mn/app/smartbank-ckbank/id1180620714",
    androidPackage: "mn.ckbank.smartbank2",
  },
  {
    key: "arigbank",
    image: FintechArigBank,
    scheme: "arig://",
    appStoreUrl: "https://apps.apple.com/mn/app/arig-online/id6444022675",
    androidPackage: "mn.arig.online",
  },
  {
    key: "mbank",
    image: FintechMBank,
    scheme: "mbank://",
    appStoreUrl: "https://apps.apple.com/mn/app/%D0%BC-bank/id1455928972",
    androidPackage: "mn.mllc.mbank",
  },
  {
    key: "hipay",
    image: FintechHiPay,
    scheme: "hipay://",
    appStoreUrl: "https://apps.apple.com/mn/app/hipay/id1530550896",
    androidPackage: "mn.highpaymentsolutions.hipay_wallet_v2",
  },
  {
    key: "capitronbank",
    image: FintechCapitronBank,
    scheme: "capitronbank://",
    appStoreUrl: "https://apps.apple.com/mn/app/capitron-digital-bank/id1612591322",
    androidPackage: "com.capitron",
  },
  {
    key: "xacbank",
    image: FintechXacBank,
    scheme: "xacbank://",
    appStoreUrl: "https://apps.apple.com/mn/app/xacbank/id1534265552",
    androidPackage: "com.xacbank.mobile",
  },
  {
    key: "statebank",
    image: FintechStateBank,
    scheme: "statebankmongolia://",
    appStoreUrl: "https://apps.apple.com/mn/app/state-bank/id6469474361",
    androidPackage: "com.statebank.gyalsbank",
  },
  {
    key: "golomtbank",
    image: FintechGolomtBank,
    scheme: "socialpay-payment://",
    appStoreUrl: "https://apps.apple.com/mn/app/socialpay/id1152919460",
    androidPackage: "mn.egolomt.socialpay",
  },
  {
    key: "khanbank",
    image: FintechKhanBank,
    scheme: "khanbank://",
    appStoreUrl: "https://apps.apple.com/mn/app/khan-bank/id1555908766",
    androidPackage: "com.khanbank.retail",
  },
  {
    key: "ard",
    image: FintechArd,
    scheme: "ard://",
    appStoreUrl: "https://apps.apple.com/mn/app/ard/id1369846744",
    androidPackage: "mn.ard.android",
  },
  {
    key: "bogd",
    image: FintechBogdBank,
    scheme: "bogdbank://",
    appStoreUrl: "https://apps.apple.com/mn/app/bogd-mobile/id1475442374",
    androidPackage: "com.bogdbank.ebank.v2",
  },
];

export default FintechAppButtons;
